import { useCallback, useEffect, useRef, useState } from 'react';

const THRESHOLD = 70;
const MAX_PULL = 90;

export const usePullToRefresh = (
    onRefresh: () => Promise<void> | void,
    containerRef: React.RefObject<Element | null>
) => {
    const [pullDistance, setPullDistance] = useState(0);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const startY = useRef(0);
    const pulling = useRef(false);
    const distRef = useRef(0);
    const refreshingRef = useRef(false);
    const onRefreshRef = useRef(onRefresh);

    useEffect(() => {
        onRefreshRef.current = onRefresh;
    }, [onRefresh]);

    useEffect(() => {
        const getScrollTop = () => containerRef.current?.scrollTop ?? 0;

        const onTouchStart = (e: TouchEvent) => {
            if (refreshingRef.current || getScrollTop() > 0) return;
            startY.current = e.touches[0].clientY;
            pulling.current = true;
        };

        const onTouchMove = (e: TouchEvent) => {
            if (!pulling.current || refreshingRef.current) return;
            if (getScrollTop() > 0) {
                pulling.current = false;
                distRef.current = 0;
                setPullDistance(0);
                return;
            }
            const dy = e.touches[0].clientY - startY.current;
            if (dy <= 0) {
                pulling.current = false;
                distRef.current = 0;
                setPullDistance(0);
                return;
            }
            const clamped = Math.min(dy * 0.5, MAX_PULL);
            distRef.current = clamped;
            setPullDistance(clamped);
        };

        const onTouchEnd = async () => {
            if (!pulling.current) return;
            const dist = distRef.current;
            pulling.current = false;
            distRef.current = 0;
            setPullDistance(0);

            if (dist >= THRESHOLD) {
                refreshingRef.current = true;
                setIsRefreshing(true);
                try {
                    await onRefreshRef.current();
                } finally {
                    refreshingRef.current = false;
                    setIsRefreshing(false);
                }
            }
        };

        document.addEventListener('touchstart', onTouchStart, { passive: true });
        document.addEventListener('touchmove', onTouchMove, { passive: true });
        document.addEventListener('touchend', onTouchEnd);

        return () => {
            document.removeEventListener('touchstart', onTouchStart);
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', onTouchEnd);
        };
    }, [containerRef]);

    return { pullDistance, isRefreshing, threshold: THRESHOLD };
};
